/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React,{FC, useEffect, useState} from "react";
import { Card, Dropdown, Spinner, Row, Col, Pagination, Form, Button  } from "react-bootstrap";
import { AVAILABLE_YEARS, HS_DIGITS } from "../../../../libs/constant";
import { getCommodities, getHSCodeDescription, getListGlobalTradeData, getRTAs } from "../core/_request";
import { ColumnDef, createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowData, SortingState, useReactTable} from '@tanstack/react-table';
import useAxiosPrivate from "../../../../libs/useAxiosPrivate";
import { RTADataTable } from "../core/_models";
import { getISO2ByISO3 } from "../../../../libs/misc";
import { useAuth } from "../../../../modules/auth";

declare module '@tanstack/react-table' { 
  interface ColumnMeta<TData extends RowData, TValue> { 
    headerClass: string; 
    cellClass:string
  }
}

const RTAsTableCard:FC = () => {
  const axiosPrivate = useAxiosPrivate()
  const { currentUser } = useAuth();
  const reporterISO3User = currentUser?.economy;
  const [isLoading, setIsLoading] = useState(false)
  const [rtaDataTable, setRTAtDataTable] = useState<RTADataTable[]>()
  const [pagination, setPagination] = React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 20,
    })
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const columnHelper = createColumnHelper<RTADataTable>()
  const columns: ColumnDef<RTADataTable, any>[] = [
    columnHelper.accessor('title',{
        header: 'Title',
        cell: info=> info.getValue(),
        meta:{
          headerClass:'ps-4 w-150px',
          cellClass:'ps-2 w-150px'
        }
      }),
      columnHelper.accessor('iso',{
        header: 'Members',
        cell: info=>{
          return info.getValue().split(';').map(iso=>{
            return (<span key={iso} className={`fi fi-${getISO2ByISO3(iso.trim())?.toLowerCase()} me-1`}></span>)
          })
        }
      }),
      columnHelper.accessor('agreement_type',{
        header: "Agreement Type",
        cell: info=>info.getValue()
      }),
      columnHelper.accessor('status',{
        header: "Status",
        cell: info=>info.getValue()
      }),
      columnHelper.accessor('composition',{
        header: "Composition",
        cell: info=>info.getValue()
      }),
      columnHelper.accessor('date_implementation',{
        header: "Date of Implementation",
        cell: info=>info.getValue(),
        meta:{
          headerClass:'w-100px',
          cellClass:'w-100px'
        }
      }),
      columnHelper.accessor('date_signed',{
        header: "Date Signed",
        cell: info=>info.getValue(),
        meta:{
          headerClass:'w-100px',
          cellClass:'w-100px'
        }
      }),
      {
        header: "Action",
        cell: info=>{
          return (<a><i className="fa-solid fa-eye"></i></a>)
        },
        meta:{
          cellClass: 'text-center',
          headerClass: ""
        }
      }
  ];

  const rtaTable = useReactTable({
    data:rtaDataTable || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: {
      pagination,
      globalFilter,
      sorting
    },
  })

  function paginationRange(dataTable) {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = dataTable.getPageCount();
    const maxPagesToShow = 1;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page, dataTable) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = dataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      dataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const pagesExportTable = paginationRange(rtaTable);
  const totalCountRTATable = rtaTable.getRowCount()

  const generateTableData = () => {
    setRTAtDataTable([])
    getRTAs(axiosPrivate,{
      rep:reporterISO3User
    })
    .then(res=>{
      const rtaData = res.data.data.rows
      return rtaData
    })
    .then(dataTable=>{
      dataTable = dataTable.map((data)=>{
        return {
          ...data,
          title: data.Agreement,
          agreement_type: data.AgreementType,
          date_implementation: data.date_of_implementation,
          composition: data.Composition,
          status: data.status,
          date_signed: data.date_signed
        }
      })
      setRTAtDataTable(dataTable)
      setIsLoading(false)
    })
  }

  useEffect(()=>{
    generateTableData()
  },[])

  const displayHeader = rtaTable.getHeaderGroups().map(headerGroup=>(
      <tr className='fw-normal py-4 text-center align-middle' key={headerGroup.id}>
      {
        headerGroup.headers.map(header=>{
          return (
            <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
              <div 
                className={header.column.getCanSort()
                    ? 'cursor-pointer select-none'
                    : ''
                }
                title={
                  header.column.getCanSort()
                    ? header.column.getNextSortingOrder() === 'asc'
                      ? 'Sort ascending'
                      : header.column.getNextSortingOrder() === 'desc'
                        ? 'Sort descending'
                        : 'Reset'
                    : undefined
                }
                onClick={header.column.getToggleSortingHandler()}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                <span className="ms-2"> 
                  {{
                    asc: (<i className="fa-solid fa-sort-up"></i>),
                    desc: (<i className="fa-solid fa-sort-down"></i>),
                    false: (<i className="fa-solid fa-sort"></i>),
                  }[header.column.getIsSorted() as string] ?? null}
                </span>
                </div>
            </th>
          )
        })
      }
      </tr>
    ))
    const displayBody = rtaTable.getRowModel().rows.map(row=>(
      <tr key={row.id} className='align-middle'>
        {
          row.getVisibleCells().map(cell=>{
            return (
              <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
                {flexRender(
                  cell.column.columnDef.cell, 
                  cell.getContext())
                }
              </td>
            )
          })
        }
      </tr>
    ))

  return (
  <Card className="card-custom">
    <Card.Header className="card-header-stretch d-flex align-items-center justify-content-between">
      <Card.Title>Trade Agreements</Card.Title>
    </Card.Header>
    <Card.Body style={{height:'600px'}}>
      {!isLoading && rtaDataTable?.length && rtaDataTable?.length ? (
        <>
          <div className='table-responsive table-fixhead'>
            <table className='gy-3 table table-striped table-bordered table-hover'>
              <thead className='table-dark'>
                {displayHeader}
              </thead>    
              <tbody className="gy-1 overflow-auto">
                {displayBody}
              </tbody>
            </table>
          </div>
          <Row className='my-5' style={{ fontSize: '0.8em' }}>
            <Col md={4} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size="sm"
                className="w-80px mx-2"
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
              results
            </Col>
            <Col md={8} className='d-flex justify-content-end'>
              <Pagination size="sm">
                <Pagination.Prev
                  onClick={() => rtaTable.previousPage()}
                  disabled={!rtaTable.getCanPreviousPage()} />
                {/* Start Dynamic Page */}
                {pagesExportTable.map((page, index) => {
                  if (page === '...') {
                    return (
                      <Pagination.Ellipsis key={index} />
                    );
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => handlePageChange(page, rtaTable)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                {/* End Dynamic Page */}
                <Pagination.Next
                  onClick={() => rtaTable.nextPage()}
                  disabled={!rtaTable.getCanNextPage()} />
              </Pagination>
            </Col>
          </Row>
        </>
      ):(
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary"></Spinner>
        </div>
      )}
    </Card.Body>
  </Card>
  )
}

export {RTAsTableCard}
